import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaCode, FaTools, FaClock, FaShieldAlt, FaHeadset } from 'react-icons/fa';

const PHPDeveloper = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-in-out',
      disable: 'mobile',
    });

    const handleScroll = () => {
      if (videoRef.current) {
        const scrollPosition = window.scrollY;
        const translateY = Math.min(scrollPosition * 0.2, 100);
        videoRef.current.style.transform = `translateY(${translateY}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <Helmet>
        <title>PHP Developer Careers | SecurXpert Technologies</title>
        <meta
          name="description"
          content="Join SecurXpert Technologies as a PHP Developer in Hyderabad. Build scalable backend applications and grow with a dynamic team."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <Navbar />

      {/* Hero Section with Parallax Video */}
      <header className="relative h-[40vh] min-h-[300px] sm:h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden" data-aos="fade">
        <video
          ref={videoRef}
          className="absolute top-0 left-0 w-full h-full object-cover sm:object-center"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/video/job.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-teal-600 opacity-40 sm:opacity-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-50"></div>
<div className="relative z-10 w-full h-full flex flex-col justify-between">
          <div className="text-left py-4 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
            <h1
              className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white wow animate__zoomIn"
              data-wow-delay="0.1s"
            >
             jobs
            </h1>
          </div>
          <div
            className="bg-white mx-auto"
            style={{
              width: "80%",
              height: "1px",
              maxWidth: "1200px",
            }}
          ></div>
          <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-6">
            <h1
              className="text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white wow animate__zoomIn text-center"
              data-wow-delay="0.3s"
            >
              PHP Developer
            </h1>
          </div>
        </div>
        </div>
      </header>

      {/* Job Details Section */}
      <main className="flex-grow py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24 w-full px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6 md:p-8" data-aos="fade-up" data-aos-delay="100">
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-800 mb-4 sm:mb-6">
              PHP Developer
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm md:text-base lg:text-lg text-gray-700 mb-4 sm:mb-6">
              <p><strong>Job Title:</strong> PHP Developer</p>
              <p><strong>Job Type:</strong> Full-Time</p>
              <p><strong>Shift Timings:</strong> Day Shift – 10:00 AM to 7:00 PM</p>
              <p><strong>Experience:</strong> 1-2 Years</p>
              <p><strong>Location:</strong> Hyderabad (Work From Office)</p>
              <p><strong>Package:</strong> Based on Current CTC</p>
              <p><strong>Number of Positions:</strong> 1</p>
              <p><strong>Joining:</strong> Immediate to 30 Days</p>
            </div>

            <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-600 mb-2 sm:mb-3 flex items-center">
              <FaCode className="text-blue-600 text-lg sm:text-xl md:text-2xl lg:text-3xl mr-2" />
              Key Accountability Areas
            </h3>
            <ul className="list-disc pl-5 text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
              <li>Develop, test, and maintain scalable backend applications using PHP.</li>
              <li>Work with frameworks like Laravel/CodeIgniter (as per project requirements).</li>
              <li>Integrate APIs, third-party services, and payment gateways.</li>
              <li>Write clean, secure, and optimized code for high performance.</li>
              <li>Manage databases (MySQL/MariaDB), queries, and schema design.</li>
              <li>Collaborate with frontend developers, QA, and product teams.</li>
              <li>Troubleshoot, debug, and upgrade existing systems.</li>
            </ul>

            <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-600 mb-2 sm:mb-3 flex items-center">
              <FaTools className="text-blue-600 text-lg sm:text-xl md:text-2xl lg:text-3xl mr-2" />
              Knowledge & Skills
            </h3>
            <ul className="list-disc pl-5 text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
              <li>Strong proficiency in PHP and OOP concepts.</li>
              <li>Hands-on experience with frameworks (Laravel/CodeIgniter preferred).</li>
              <li>Good understanding of MySQL, database optimization, and query performance.</li>
              <li>Familiarity with RESTful APIs, JSON, and web services.</li>
              <li>Knowledge of HTML, CSS, JavaScript (basic frontend integration).</li>
              <li>Experience with Git, version control, and deployment practices.</li>
              <li>Bonus: Knowledge of cloud hosting (AWS, DigitalOcean) and security best practices.</li>
            </ul>

            <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-600 mb-2 sm:mb-3 flex items-center">
              <FaHeadset className="text-blue-600 text-lg sm:text-xl md:text-2xl lg:text-3xl mr-2" />
              Interpersonal & Communication Skills
            </h3>
            <ul className="list-disc pl-5 text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
              <li>Strong problem-solving and debugging skills.</li>
              <li>Ability to manage time and deliver within deadlines.</li>
              <li>Team player with good communication skills.</li>
              <li>Willingness to learn and adapt to new technologies.</li>
            </ul>

            <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-600 mb-2 sm:mb-3 flex items-center">
              <FaShieldAlt className="text-blue-600 text-lg sm:text-xl md:text-2xl lg:text-3xl mr-2" />
              Education Qualification
            </h3>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
              Bachelor’s degree in Computer Science, Information Technology, B.E., or B.Tech. Equivalent experience or certifications in PHP development will also be considered.
            </p>

             <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-600 mb-2 sm:mb-3 flex items-center">
              <FaClock className="text-blue-600 text-lg sm:text-xl md:text-2xl lg:text-3xl mr-2" />
              About SecurXpert Technologies
            </h3>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6">
              SecurXpert Technologies is a Hyderabad-based IT solutions company focused on delivering innovative, scalable, and client-centric digital solutions. We empower businesses with technology that simplifies operations and drives growth.
            </p>

            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 mb-4 sm:mb-6 font-semibold">
              If you’re a PHP Developer looking for your next challenge and can join immediately or within 30 days, we’d love to connect with you!
            </p>

            <Link
              to="/careers"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-3 rounded-lg bg-blue-600 text-white text-sm sm:text-base md:text-lg font-semibold hover:bg-blue-700 transition no-underline hover:scale-105"
            >
              Apply Now
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </main>

      <Footer />

      {/* Back to Top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-3 sm:bottom-4 md:bottom-6 right-3 sm:right-4 md:right-6 bg-blue-600 hover:bg-blue-700 p-2 sm:p-3 md:p-4 rounded-full shadow-lg transition"
        aria-label="Back to top"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @media (max-width: 640px) {
          .scrollbar-hide {
            overflow-x: scroll;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </div>
  );
};

export default PHPDeveloper;